import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  Component,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { Router } from '@angular/router'
import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'
import { SplashScreen } from '@capacitor/splash-screen'
import { IonicModule, Platform } from '@ionic/angular'
import { Subject, takeUntil } from 'rxjs'
import { environment } from 'src/environments/environment'
import { register as registerSwiperElement } from 'swiper/element/bundle'
import { AppRoute } from './app.routes.fixtures'
import { AppStateService } from './services/app-state/app-state.service'
import { AuthService } from './services/auth/auth.service'
import { LanguageService } from './services/language/language.service'
import { NotificationService } from './services/notification/notification.service'
import { ThemeService } from './services/theme/theme.service'
import { AnalyticsService } from './services/analytics/analytics.service'

const DEBUG_APP_LISTENERS = false

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private _destroySubject$: Subject<void> = new Subject<void>()
  private isWindowOpenOverridden: boolean = false

  constructor(
    private platform: Platform,
    private router: Router,
    private zone: NgZone,
    private authService: AuthService,
    private notificationService: NotificationService,
    private appStateService: AppStateService,
    private themeService: ThemeService,
    analyticsService: AnalyticsService,
    languageService: LanguageService
  ) {
    const searchParams = new URLSearchParams(window.location.search)
    const utmSource = searchParams.get('utm_source')
    const utmMedium = searchParams.get('utm_medium')
    const utmCamapign = searchParams.get('utm_campaign')
    const utmContent = searchParams.get('utm_content')

    if (utmSource) {
      analyticsService.logEvent('visit_from_ad', {
        source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCamapign,
        utm_content: utmContent,
      })
    }

    // register swiper-element once globally
    registerSwiperElement()
    languageService.initTranslations()
    themeService.updateTheme()
    this.overrideWindowOpen()
  }

  ngOnInit() {
    this.authService.initAuthConfiguration()
    this.initializeApp()
  }

  ngOnDestroy() {
    this._destroySubject$.next()
    this._destroySubject$.complete()
  }

  ngAfterViewInit() {
    setTimeout(async () => {
      await this.themeService.updateAppBars()
      await SplashScreen.hide({ fadeOutDuration: 500 })
    }, 500)
  }

  async initializeApp(): Promise<void> {
    await this.platform.ready()

    App.addListener('appStateChange', (state) => {
      this.appStateService.isAppActive.next(state.isActive)
    })

    App.addListener('appUrlOpen', (data: any) => {
      if (DEBUG_APP_LISTENERS) {
        console.log('appUrlOpen: ' + data.url)
      }
      this.zone.run(() => {
        const prefix = environment.appScheme
        const slug = data.url.startsWith(prefix)

        if (slug) {
          const path: string = data.url.substr(prefix.length)
          const localUrl = 'http://localhost' + path

          // subscribe for LoginResponse to make sure, that it will be done
          this.authService
            .checkAuth(localUrl)
            .pipe(takeUntil(this._destroySubject$))
            .subscribe(async (auth) => {
              // close internal browser in ios (not self-closing)
              if (auth.isAuthenticated) {
                await Browser.close()
              }

              // forward e.g. sciper://intro to intro-page
              const route = path.includes('?')
                ? path.slice(0, path.indexOf('?'))
                : path
              const isAppRoute = Object.keys(AppRoute).includes(route)
              if (!isAppRoute || this.router.url.includes(route)) {
                return
              }
              this.router.navigate([route], {
                replaceUrl: true,
              })
            })
        }
        // If no match, do nothing - let regular routing logic take over
      })
    })

    this.notificationService.initialize()
  }

  private overrideWindowOpen() {
    // Override window.open to force internal browser when login is opened as a frame (mobile apps)
    // reference: https://stackoverflow.com/a/9172526/5742907

    if (this.isWindowOpenOverridden || !Capacitor.isNativePlatform()) {
      return
    }
    this.isWindowOpenOverridden = true

    window.open = function (url?: string | URL) {
      if (DEBUG_APP_LISTENERS) {
        console.log('window.open (native) ' + url)
      }
      Browser.open({
        url: (url as URL)?.toString() ?? (url as string) ?? '',
        windowName: 'appLogin',
        presentationStyle: 'popover',
      })
      return window
    }
  }

  // HostListener for html-tags with format
  //  <a routerlink="abc"></a>
  // to navigate using angular router
  @HostListener('document:click', ['$event'])
  clickout(_event: Event): boolean {
    const event = _event || window.event
    const element = event?.target || event?.srcElement
    if (!element) {
      return true
    }
    const htmlElement = element as HTMLElement

    if (!htmlElement) {
      return true
    }

    if (htmlElement.tagName !== 'A') {
      return true
    }

    const routerlink = htmlElement?.attributes.getNamedItem('routerlink')

    if (routerlink && routerlink.value) {
      event.stopPropagation()
      event.preventDefault()
      this.router.navigate([routerlink.value])
      return false
    }

    return true
  }
}
